/**
 * Event-kind classification — THE single place where the raw
 * `eventType × timeSlotType × isGroupTherapist` matrix is interpreted.
 *
 * The old calendar's `EventContent` decides what to render through nested
 * if/else chains over raw enum values, scattered across 700+ lines. Here the
 * decision is made ONCE, in the model, at index-build time; everything
 * downstream (content registry, grouping bars, future context menus)
 * dispatches on the closed `MCEventKind` union instead of re-deriving it.
 */
import type { EventType, TimeSlotType } from "../../Calendar/controller/adapters/types";

export type MCEventKind =
  /** Patient treatment slot (incl. continuous series) */
  | "appointment"
  /** Group therapist block carrying participants */
  | "group"
  /** Pause / lunch break */
  | "break"
  /** Drive time between visits (Weg), incl. draft variants */
  | "travel"
  /** Whole/partial-day absences: Urlaub, Krank, GoneFishing, CancelledDay */
  | "absence"
  /** Planning-mode draft placements/shifts */
  | "draft"
  /** Named events, sales slots and anything unrecognized */
  | "generic";

const KIND_BY_TIME_SLOT_TYPE: Partial<Record<TimeSlotType, MCEventKind>> = {
  LunchBreak: "break",
  TravelTime: "travel",
  DraftTravelTime: "travel",
  LeaveOfAbsence: "absence",
  GoneFishing: "absence",
  CancelledDay: "absence",
  DraftPlacement: "draft",
  DraftShift: "draft",
};

/**
 * Precedence: group therapist beats everything (its timeSlotType is a group
 * variant anyway), then structural timeSlotTypes (a Pause is a Pause even if
 * some adapter mislabels its eventType), then the adapter-level eventType.
 *
 * Data reality: drive-time blocks are mostly created as NAMED EventTimeSlots
 * titled "Weg" rather than TravelTime slots — the title heuristic lives here,
 * in the one classification choke point, not in any renderer.
 */
export const kindOfEvent = (
  eventType: EventType,
  timeSlotType: TimeSlotType,
  isGroupTherapist: boolean,
  title?: string,
): MCEventKind => {
  if (isGroupTherapist) return "group";
  const structural = KIND_BY_TIME_SLOT_TYPE[timeSlotType];
  if (structural) return structural;
  if (eventType === "appointment") return "appointment";
  if (eventType === "draft") return "draft";
  if (title?.trim().toLowerCase() === "weg") return "travel";
  return "generic";
};
